import React, { useState, useEffect } from 'react';
import { Shield, User, Lock, Database, Users, Clock, FileText, Globe, Mail } from 'lucide-react';
import { InfoPageHeader } from '@/components/brand/InfoPageHeader';

const PrivacyPolicy = () => {
  // Initialize language from localStorage or default to Latvian
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language') || localStorage.getItem('selectedLanguage');
    return savedLanguage || 'lv';
  });

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    en: {
      title: "Privacy Policy",
      subtitle: "How We Protect Your Personal Data",
      intro: {
        title: "1. General Provisions",
        text: "This Privacy Policy explains how SIA \"Hermes invest\" (hereinafter - the Company) processes and protects customer personal data when placing orders through the website www.rozedinapoli.lv. By using our website, you agree to the terms of this Policy."
      },
      controller: {
        title: "Data Controller",
        company: "SIA \"Hermes invest\"",
        regNo: "Reg. No. LV42103078050",
        address: "Legal Address: Vecā Ostmala iela 54, Liepāja, Latvija",
        director: "Board Member: Oleg Chramov",
        email: "info@rozedinapoli.lv",
        phone: "+371 28717331",
        bank: "Bank: BluOr Bank AS",
        swift: "SWIFT: CBBRLV22",
        account: "Account: LV32CBBR1124232600010"
      },
      dataCollected: {
        title: "2. What Data We Collect",
        items: [
          "Name, surname",
          "Phone number, delivery address",
          "Email address",
          "Order and payment information",
          "IP address, cookies, and technical information about website interaction"
        ]
      },
      purposes: {
        title: "3. Purposes of Data Processing",
        text: "We process data to:",
        items: [
          "Process and deliver your order",
          "Contact you to clarify details",
          "Send confirmation and receipt",
          "Improve service quality and website functionality",
          "With consent - inform about news and promotions"
        ]
      },
      legalBasis: {
        title: "4. Legal Basis for Processing",
        text: "Personal data is processed based on:",
        items: [
          "Contract fulfillment (order processing and delivery)",
          "Legitimate interests of the company (service improvement, error prevention)",
          "Customer consent (e.g., receiving news and offers)"
        ]
      },
      sharing: {
        title: "5. Who May Receive Data",
        text: "Data may be shared with:",
        items: [
          "Courier and payment services involved in order fulfillment",
          "IT providers ensuring website operation",
          "Government agencies if required by law"
        ],
        note: "We do NOT share personal data with third parties for advertising purposes without your consent."
      },
      retention: {
        title: "6. Data Retention Period",
        text: "Personal data is stored only as long as necessary for:",
        items: [
          "Order fulfillment and delivery",
          "Accounting purposes",
          "Compliance with legal requirements"
        ],
        note: "After this period, data is deleted or anonymized."
      },
      rights: {
        title: "7. Your Rights",
        text: "You have the right to:",
        items: [
          "Request information about what data we store",
          "Request correction, deletion, or restriction of processing",
          "Withdraw your consent",
          "File a complaint with the State Data Inspectorate of Latvia (www.dvi.gov.lv)"
        ],
        contact: "To exercise these rights, contact us at: info@rozedinapoli.lv"
      },
      security: {
        title: "8. Data Security",
        text: "The Company applies modern technical and organizational data protection measures:",
        items: [
          "Secure servers and SSL encryption",
          "Limited employee access",
          "Use of licensed software only and regular updates"
        ],
        note: "We guarantee that your data is processed lawfully, fairly, and transparently."
      },
      transfers: {
        title: "9. Data Transfer to Third Countries",
        text: "The Company does NOT transfer personal data outside the European Union and European Economic Area."
      },
      contact: {
        title: "10. Privacy Contacts",
        text: "If you have questions about personal data protection, contact us:",
        email: "info@rozedinapoli.lv",
        address: "Vecā Ostmala iela 54, Liepāja, LV-3401"
      },
      footer: {
        copyright: "All rights reserved.",
        tagline: "Fresh pizza. Fast delivery. Reliable data protection."
      }
    },
    ru: {
      title: "Политика конфиденциальности",
      subtitle: "Как мы защищаем ваши персональные данные",
      intro: {
        title: "1. Общие положения",
        text: "Настоящая Политика конфиденциальности объясняет, как SIA \"Hermes invest\" (далее – Компания) обрабатывает и защищает персональные данные клиентов при оформлении заказов через сайт www.rozedinapoli.lv. Используя наш сайт, вы соглашаетесь с условиями настоящей Политики."
      },
      controller: {
        title: "Контролёр данных",
        company: "SIA \"Hermes invest\"",
        regNo: "Рег. № LV42103078050",
        address: "Юридический адрес: Vecā Ostmala iela 54, Liepāja, Latvija",
        director: "Член правления: Oleg Chramov",
        email: "info@rozedinapoli.lv",
        phone: "+371 28717331",
        bank: "Банк: BluOr Bank AS",
        swift: "SWIFT: CBBRLV22",
        account: "Счёт: LV32CBBR1124232600010"
      },
      dataCollected: {
        title: "2. Какие данные мы собираем",
        items: [
          "Имя, фамилия",
          "Номер телефона, адрес доставки",
          "Адрес электронной почты",
          "Данные о заказах и оплате",
          "IP-адрес, cookies и техническую информацию о взаимодействии с сайтом"
        ]
      },
      purposes: {
        title: "3. Цели обработки данных",
        text: "Мы обрабатываем данные, чтобы:",
        items: [
          "Оформить и доставить ваш заказ",
          "Связаться с вами при уточнении деталей",
          "Отправить подтверждение и чек",
          "Улучшить качество обслуживания и работу сайта",
          "При наличии согласия — информировать о новостях и акциях"
        ]
      },
      legalBasis: {
        title: "4. Правовые основания обработки",
        text: "Персональные данные обрабатываются на основании:",
        items: [
          "Выполнения договора (оформление и доставка заказа)",
          "Законных интересов компании (улучшение сервиса, предотвращение ошибок)",
          "Согласия клиента (например, на получение новостей и предложений)"
        ]
      },
      sharing: {
        title: "5. Кому могут быть переданы данные",
        text: "Данные могут быть переданы:",
        items: [
          "Курьерским и платёжным службам, участвующим в выполнении заказа",
          "IT-провайдерам, обеспечивающим работу сайта",
          "Государственным учреждениям, если это требуется законом"
        ],
        note: "Мы НЕ передаём персональные данные третьим лицам в рекламных целях без вашего согласия."
      },
      retention: {
        title: "6. Срок хранения данных",
        text: "Персональные данные хранятся только столько, сколько необходимо для:",
        items: [
          "Выполнения заказа и доставки",
          "Ведения бухгалтерского учёта",
          "Выполнения требований законодательства"
        ],
        note: "По истечении этого срока данные удаляются или анонимизируются."
      },
      rights: {
        title: "7. Ваши права",
        text: "Вы имеете право:",
        items: [
          "Запросить информацию о том, какие данные мы храним",
          "Потребовать исправления, удаления или ограничения обработки",
          "Отозвать своё согласие",
          "Подать жалобу в Государственную инспекцию данных Латвии (www.dvi.gov.lv)"
        ],
        contact: "Для реализации этих прав свяжитесь с нами: info@rozedinapoli.lv"
      },
      security: {
        title: "8. Безопасность данных",
        text: "Компания применяет современные технические и организационные меры защиты данных:",
        items: [
          "Защищённые серверы и SSL-шифрование",
          "Ограниченный доступ сотрудников",
          "Использование только лицензированного ПО и регулярные обновления"
        ],
        note: "Мы гарантируем, что ваши данные обрабатываются законно, добросовестно и прозрачно."
      },
      transfers: {
        title: "9. Передача данных третьим странам",
        text: "Компания НЕ передаёт персональные данные за пределы Европейского Союза и Европейской экономической зоны."
      },
      contact: {
        title: "10. Контакты по вопросам конфиденциальности",
        text: "Если у вас есть вопросы о защите персональных данных, обращайтесь:",
        email: "info@rozedinapoli.lv",
        address: "Vecā Ostmala iela 54, Liepāja, LV-3401"
      },
      footer: {
        copyright: "Все права защищены.",
        tagline: "Свежая пицца. Быстрая доставка. Надёжная защита ваших данных."
      }
    },
    lv: {
      title: "Privātuma politika",
      subtitle: "Kā mēs aizsargājam jūsu personas datus",
      intro: {
        title: "1. Vispārīgie noteikumi",
        text: "Šī Privātuma politika izskaidro, kā SIA \"Hermes invest\" (turpmāk - Uzņēmums) apstrādā un aizsargā klientu personas datus, veicot pasūtījumus vietnē www.rozedinapoli.lv. Izmantojot mūsu vietni, jūs piekrītat šīs Politikas noteikumiem."
      },
      controller: {
        title: "Datu pārzinis",
        company: "SIA \"Hermes invest\"",
        regNo: "Reģ. Nr. LV42103078050",
        address: "Juridiskā adrese: Vecā Ostmala iela 54, Liepāja, Latvija",
        director: "Valdes loceklis: Oleg Chramov",
        email: "info@rozedinapoli.lv",
        phone: "+371 28717331",
        bank: "Banka: BluOr Bank AS",
        swift: "SWIFT: CBBRLV22",
        account: "Konts: LV32CBBR1124232600010"
      },
      dataCollected: {
        title: "2. Kādus datus mēs vācam",
        items: [
          "Vārds, uzvārds",
          "Tālruņa numurs, piegādes adrese",
          "E-pasta adrese",
          "Informācija par pasūtījumiem un maksājumiem",
          "IP adrese, sīkdatnes un tehniskā informācija par mijiedarbību ar vietni"
        ]
      },
      purposes: {
        title: "3. Datu apstrādes mērķi",
        text: "Mēs apstrādājam datus, lai:",
        items: [
          "Apstrādātu un piegādātu jūsu pasūtījumu",
          "Sazinātos ar jums, lai precizētu detaļas",
          "Nosūtītu apstiprinājumu un kvīti",
          "Uzlabotu pakalpojumu kvalitāti un vietnes funkcionalitāti",
          "Ar piekrišanu - informētu par jaunumiem un akcijām"
        ]
      },
      legalBasis: {
        title: "4. Datu apstrādes juridiskais pamats",
        text: "Personas dati tiek apstrādāti, pamatojoties uz:",
        items: [
          "Līguma izpildi (pasūtījuma apstrāde un piegāde)",
          "Uzņēmuma likumīgām interesēm (pakalpojumu uzlabošana, kļūdu novēršana)",
          "Klienta piekrišanu (piemēram, ziņu un piedāvājumu saņemšanai)"
        ]
      },
      sharing: {
        title: "5. Kam var tikt nodoti dati",
        text: "Dati var tikt nodoti:",
        items: [
          "Kurjeru un maksājumu pakalpojumiem, kas iesaistīti pasūtījuma izpildē",
          "IT pakalpojumu sniedzējiem, kas nodrošina vietnes darbību",
          "Valsts iestādēm, ja to pieprasa likums"
        ],
        note: "Mēs NENODODAM personas datus trešajām personām reklāmas nolūkos bez jūsu piekrišanas."
      },
      retention: {
        title: "6. Datu glabāšanas termiņš",
        text: "Personas dati tiek glabāti tikai tik ilgi, cik nepieciešams:",
        items: [
          "Pasūtījuma izpildei un piegādei",
          "Grāmatvedības vajadzībām",
          "Likumdošanas prasību izpildei"
        ],
        note: "Pēc šī termiņa beigām dati tiek dzēsti vai anonimizēti."
      },
      rights: {
        title: "7. Jūsu tiesības",
        text: "Jums ir tiesības:",
        items: [
          "Pieprasīt informāciju par to, kādus datus mēs glabājam",
          "Pieprasīt labošanu, dzēšanu vai apstrādes ierobežošanu",
          "Atsaukt savu piekrišanu",
          "Iesniegt sūdzību Latvijas Datu valsts inspekcijai (www.dvi.gov.lv)"
        ],
        contact: "Lai īstenotu šīs tiesības, sazinieties ar mums: info@rozedinapoli.lv"
      },
      security: {
        title: "8. Datu drošība",
        text: "Uzņēmums piemēro mūsdienīgus tehniskos un organizatoriskos datu aizsardzības pasākumus:",
        items: [
          "Drošus serverus un SSL šifrēšanu",
          "Ierobežotu darbinieku piekļuvi",
          "Tikai licencētas programmatūras izmantošanu un regulārus atjauninājumus"
        ],
        note: "Mēs garantējam, ka jūsu dati tiek apstrādāti likumīgi, godīgi un pārskatāmi."
      },
      transfers: {
        title: "9. Datu nodošana trešajām valstīm",
        text: "Uzņēmums NENODOD personas datus ārpus Eiropas Savienības un Eiropas Ekonomikas zonas."
      },
      contact: {
        title: "10. Privātuma kontakti",
        text: "Ja jums ir jautājumi par personas datu aizsardzību, sazinieties ar mums:",
        email: "info@rozedinapoli.lv",
        address: "Vecā Ostmala iela 54, Liepāja, LV-3401"
      },
      footer: {
        copyright: "Visas tiesības aizsargātas.",
        tagline: "Svaiga pica. Ātra piegāde. Uzticama datu aizsardzība."
      }
    }
  };

  const t = content[language];

  return (
    <div className="rz-page min-h-screen" data-testid="privacy-page">
      <InfoPageHeader language={language} setLanguage={setLanguage} title={t.title} subtitle={t.subtitle} />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-6">
          
          {/* Introduction */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.intro.title}
                </h2>
                <p className="text-[#57534E] leading-relaxed">
                  {t.intro.text}
                </p>
              </div>
            </div>
          </div>

          {/* Data Controller */}
          <div className="bg-[#F5F2EC] border border-[#E7E5E4] overflow-hidden p-8">
            <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4 text-center">
              {t.controller.title}
            </h2>
            <div className="space-y-2 text-[#57534E]">
              <p className="font-semibold text-lg">{t.controller.company}</p>
              <p>{t.controller.regNo}</p>
              <p>{t.controller.address}</p>
              <p>{t.controller.director}</p>
              <p className="flex items-center space-x-2 mt-4">
                <Mail className="w-4 h-4 text-[#C2410C]" />
                <a href="mailto:info@rozedinapoli.lv" className="text-[#1C1917] hover:underline">
                  {t.controller.email}
                </a>
              </p>
              <p>{t.controller.phone}</p>
              <div className="mt-4 pt-4 border-t border-[#E7E5E4]">
                <p>{t.controller.bank}</p>
                <p>{t.controller.swift}</p>
                <p className="font-mono text-sm">{t.controller.account}</p>
              </div>
            </div>
          </div>

          {/* Data Collected */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <Database className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.dataCollected.title}
                </h2>
                <ul className="space-y-2">
                  {t.dataCollected.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#1C1917] mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Purposes */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.purposes.title}
                </h2>
                <p className="text-[#57534E] mb-3">{t.purposes.text}</p>
                <ul className="space-y-2">
                  {t.purposes.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#1C1917] mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Legal Basis */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.legalBasis.title}
                </h2>
                <p className="text-[#57534E] mb-3">{t.legalBasis.text}</p>
                <ul className="space-y-2">
                  {t.legalBasis.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#1C1917] mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Data Sharing */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.sharing.title}
                </h2>
                <p className="text-[#57534E] mb-3">{t.sharing.text}</p>
                <ul className="space-y-2 mb-4">
                  {t.sharing.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#1C1917] mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm bg-[#F5F2EC] p-3 rounded-none text-[#57534E]">
                  <strong>Note:</strong> {t.sharing.note}
                </p>
              </div>
            </div>
          </div>

          {/* Retention */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.retention.title}
                </h2>
                <p className="text-[#57534E] mb-3">{t.retention.text}</p>
                <ul className="space-y-2 mb-4">
                  {t.retention.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-indigo-600 mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm bg-indigo-50 p-3 rounded-none text-[#57534E]">
                  {t.retention.note}
                </p>
              </div>
            </div>
          </div>

          {/* Rights */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-[#C2410C]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.rights.title}
                </h2>
                <p className="text-[#57534E] mb-3">{t.rights.text}</p>
                <ul className="space-y-2 mb-4">
                  {t.rights.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#C2410C] mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm bg-[#F5F2EC] p-3 rounded-none text-[#57534E]">
                  {t.rights.contact}
                </p>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <Lock className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.security.title}
                </h2>
                <p className="text-[#57534E] mb-3">{t.security.text}</p>
                <ul className="space-y-2 mb-4">
                  {t.security.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#1C1917] mr-2">•</span>
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm bg-[#F5F2EC] p-3 rounded-none text-[#57534E]">
                  {t.security.note}
                </p>
              </div>
            </div>
          </div>

          {/* Transfers */}
          <div className="bg-white border border-[#E7E5E4] p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <Globe className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.transfers.title}
                </h2>
                <p className="text-[#57534E]">{t.transfers.text}</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-[#F5F2EC] border border-[#E7E5E4] overflow-hidden p-8">
            <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4 text-center">
              {t.contact.title}
            </h2>
            <p className="text-[#57534E] text-center mb-4">{t.contact.text}</p>
            <div className="text-center space-y-2">
              <p>
                <Mail className="w-4 h-4 inline mr-2 text-[#C2410C]" />
                <a href="mailto:info@rozedinapoli.lv" className="text-[#1C1917] hover:underline font-semibold">
                  {t.contact.email}
                </a>
              </p>
              <p className="text-[#57534E]">{t.contact.address}</p>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-[#57534E] space-y-2">
          <p className="font-semibold">
            © 2025 SIA "Hermes invest" / Roze di Napoli. {t.footer.copyright}
          </p>
          <p className="text-sm">
            🍕 {t.footer.tagline}
          </p>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a 
            href="/" 
            className="inline-flex items-center text-[#57534E] hover:text-[#1C1917] transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
